import React, { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';

/**
 * AuthModal - Modal de seguridad para acciones críticas.
 * Corregido: Uso de createPortal para evitar errores de renderizado de objetos DOM.
 * Valida mediante la contraseña maestra: marcosib1234
 */
const AuthModal = ({ isOpen, onClose, onConfirm, title, message, requireAuth = true }) => {
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [isProcessing, setIsProcessing] = useState(false);

  // Clave de seguridad única solicitada
  const MASTER_PASSWORD = 'marcosib1234';

  // Limpiar el campo cuando el modal se cierra o abre
  useEffect(() => {
    if (!isOpen) {
      setPassword('');
      setError('');
    }
  }, [isOpen]);

  const handleConfirm = async () => {
    setError('');

    if (requireAuth) {
      if (!password) {
        setError('Por favor, ingrese la contraseña de seguridad.');
        return;
      }

      if (password !== MASTER_PASSWORD) {
        setError('LA CONTRASEÑA ES INCORRECTA. ACCIÓN NO AUTORIZADA.');
        return;
      }
    }

    try {
      setIsProcessing(true);
      await onConfirm(); // Ejecuta la función (borrar o rehabilitar)
      onClose(); 
    } catch (err) {
      console.error(err);
      setError('Ocurrió un error al procesar la solicitud.');
    } finally {
      setIsProcessing(false);
    }
  };

  if (!isOpen) return null;

  // IMPORTANTE: Se usa createPortal para renderizar fuera del flujo normal de la app
  return createPortal(
    <div className="fixed inset-0 bg-slate-900/60 overflow-y-auto h-full w-full z-[2000] flex items-center justify-center backdrop-blur-sm animate-fade-in text-left font-['Inter']">
      <div className="relative p-8 border-none w-full max-w-md shadow-2xl rounded-2xl bg-white scale-in">
        
        {/* Encabezado e Icono */}
        <div className="text-center mb-6">
          <div className="mx-auto flex items-center justify-center h-14 w-14 rounded-full bg-red-50 mb-4 border border-red-100 text-left">
            <svg className="h-7 w-7 text-red-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2.5" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
            </svg>
          </div>
          <h3 className="text-xl leading-6 font-bold text-gray-900 tracking-tight text-center">
            {title || 'Confirmar Acción'}
          </h3>
          <p className="text-sm text-gray-500 mt-2 font-normal text-center">
            {message || 'Esta acción requiere autorización de seguridad.'}
          </p>
        </div>

        {/* Input de Contraseña */}
        {requireAuth && (
          <div className="mt-4 px-2 text-center">
            <label className="block text-[10px] font-black text-gray-400 uppercase tracking-widest mb-1.5 ml-1">Contraseña de Seguridad</label>
            <input 
              type="password" 
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && handleConfirm()}
              placeholder="••••••••••••" 
              autoFocus
              className="w-full bg-slate-50 border-gray-200 border-2 rounded-xl py-3 px-4 shadow-sm focus:ring-4 focus:ring-red-500/10 focus:border-red-500 outline-none transition-all font-mono text-center text-lg tracking-widest"
            />
          </div>
        )}

        {/* Mensaje de Error */}
        {error && (
          <div className="mt-4 p-3 bg-red-50 border border-red-100 rounded-xl animate-shake">
            <p className="text-red-600 text-[11px] text-center font-black uppercase tracking-tighter">{error}</p>
          </div>
        )}

        {/* Botones de Acción */}
        <div className="flex flex-col sm:flex-row justify-center mt-8 gap-3">
          <button 
            onClick={onClose} 
            disabled={isProcessing}
            className="order-2 sm:order-1 flex-1 px-6 py-3 bg-slate-100 text-slate-600 rounded-xl hover:bg-slate-200 disabled:opacity-50 font-bold transition-all uppercase text-xs tracking-widest"
          >
            Cancelar
          </button>
          <button 
            onClick={handleConfirm} 
            disabled={isProcessing}
            className="order-1 sm:order-2 flex-[1.5] px-6 py-3 bg-red-600 text-white rounded-xl hover:bg-red-700 disabled:opacity-50 font-black shadow-lg shadow-red-200 transition-all transform active:scale-95 uppercase text-xs tracking-widest"
          >
            {isProcessing ? 'Validando...' : 'Confirmar Acción'}
          </button>
        </div>
      </div>
      
      <style>{`
        .animate-fade-in { animation: fadeIn 0.2s ease-out forwards; }
        .scale-in { animation: scaleIn 0.3s cubic-bezier(0.16, 1, 0.3, 1) forwards; }
        .animate-shake { animation: shake 0.4s cubic-bezier(.36,.07,.19,.97) both; }
        @keyframes fadeIn { from { opacity: 0; } to { opacity: 1; } }
        @keyframes scaleIn { from { opacity: 0; transform: scale(0.95); } to { opacity: 1; transform: scale(1); } }
        @keyframes shake {
          10%, 90% { transform: translate3d(-1px, 0, 0); }
          20%, 80% { transform: translate3d(2px, 0, 0); }
          30%, 50%, 70% { transform: translate3d(-4px, 0, 0); }
          40%, 60% { transform: translate3d(4px, 0, 0); }
        }
      `}</style>
    </div>,
    document.body
  );
};

export default AuthModal;